import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClickOutside } from '../../lib/useClickOutside';
import {
  ROLLOUT_MASTER_GRID_KEY,
  type CurrentUser,
  type GridPreferences,
} from '../../types/user';
import { patchMyPreferences } from './api';
import {
  ALL_COLUMN_KEYS,
  FACTORY_ORDER,
  sanitizeOrder,
  type ColumnKey,
} from './columns';

// Built-in 'default' preset (spec §7.1): all columns visible in factory order.
// Never persisted -- computed from the column catalogue every time.
export const DEFAULT_PRESET_ID = 'default';
const MAX_USER_PRESETS = 3;

type Props = {
  currentUser: CurrentUser;
  columnOrder: readonly ColumnKey[];
  visibleColumns: ReadonlySet<ColumnKey>;
  onApply: (order: ColumnKey[], visible: Set<ColumnKey>) => void;
};

// Snapshot current column state to the wire format: the list of visible
// columns in their current display order. Hidden columns are simply absent
// from the list -- combined with sanitizeOrder on load, unknown/future factory
// keys land in the order tail but stay hidden.
function snapshotPreset(
  order: readonly ColumnKey[],
  visible: ReadonlySet<ColumnKey>,
): string[] {
  return order.filter((k) => visible.has(k));
}

// Apply a saved preset string[] into concrete order + visible set. Unknown
// keys are silent-filtered on the client (server also tolerates them via the
// stored shape; columns.ts#sanitizeOrder is the sieve).
function applyStoredPreset(stored: readonly string[]): {
  order: ColumnKey[];
  visible: Set<ColumnKey>;
} {
  const visible = new Set<ColumnKey>();
  for (const k of stored) {
    if (ALL_COLUMN_KEYS.has(k as ColumnKey)) visible.add(k as ColumnKey);
  }
  const { order } = sanitizeOrder(stored);
  return { order, visible };
}

function defaultState(): { order: ColumnKey[]; visible: Set<ColumnKey> } {
  return { order: [...FACTORY_ORDER], visible: new Set(ALL_COLUMN_KEYS) };
}

export function ColumnPresetMenu({
  currentUser,
  columnOrder,
  visibleColumns,
  onApply,
}: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const prefs = currentUser.gridPreferences?.[ROLLOUT_MASTER_GRID_KEY];
  const activeId = prefs?.activePresetId ?? DEFAULT_PRESET_ID;
  const userPresets = prefs?.presets ?? [];

  const activeLabel = useMemo(() => {
    if (activeId === DEFAULT_PRESET_ID) return 'Default view';
    const p = userPresets.find((p) => p.id === activeId);
    return p?.name ?? 'Default view';
  }, [activeId, userPresets]);

  const saveMut = useMutation({
    mutationFn: (next: GridPreferences) =>
      patchMyPreferences(ROLLOUT_MASTER_GRID_KEY, next),
    onSuccess: (user) => {
      qc.setQueryData(['me'], user);
    },
  });

  const persist = async (next: GridPreferences) => {
    // Optimistic UX: patch runs async; errors surface via the mutation's error
    // state (not currently shown -- toast polish is P5). On success the query
    // cache is updated so future reads reflect the new prefs.
    try {
      await saveMut.mutateAsync(next);
    } catch (err) {
      // Revert UI by forcing the currentUser cache back to stale; the guard
      // will refetch on next mount. We don't rollback the local column state
      // because doing so would feel janky mid-interaction -- the next page
      // load will reconcile.
      console.error('Failed to save rollout grid preferences', err);
    }
  };

  const buildPrefs = (nextUserPresets: typeof userPresets, nextActiveId: string) => ({
    activePresetId: nextActiveId,
    presets: nextUserPresets,
  });

  const switchPreset = (id: string) => {
    if (id === DEFAULT_PRESET_ID) {
      const { order, visible } = defaultState();
      onApply(order, visible);
    } else {
      const p = userPresets.find((p) => p.id === id);
      if (!p) return;
      const { order, visible } = applyStoredPreset(p.visibleColumns);
      onApply(order, visible);
    }
    setOpen(false);
    void persist(buildPrefs(userPresets, id));
  };

  const saveAsNew = () => {
    if (userPresets.length >= MAX_USER_PRESETS) return;
    const name = window.prompt('Name this preset');
    if (!name || !name.trim()) return;
    const id =
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const nextPresets = [
      ...userPresets,
      {
        id,
        name: name.trim(),
        visibleColumns: snapshotPreset(columnOrder, visibleColumns),
      },
    ];
    setOpen(false);
    void persist(buildPrefs(nextPresets, id));
  };

  const saveChanges = () => {
    if (activeId === DEFAULT_PRESET_ID) return;
    const nextPresets = userPresets.map((p) =>
      p.id === activeId
        ? { ...p, visibleColumns: snapshotPreset(columnOrder, visibleColumns) }
        : p,
    );
    setOpen(false);
    void persist(buildPrefs(nextPresets, activeId));
  };

  const deleteCurrent = () => {
    if (activeId === DEFAULT_PRESET_ID) return;
    if (!window.confirm('Delete this preset? The columns stay as they are right now.')) return;
    const nextPresets = userPresets.filter((p) => p.id !== activeId);
    setOpen(false);
    void persist(buildPrefs(nextPresets, DEFAULT_PRESET_ID));
    // Column state stays as-is per the confirm copy.
  };

  const resetToDefault = () => {
    const { order, visible } = defaultState();
    onApply(order, visible);
    setOpen(false);
    void persist(buildPrefs(userPresets, DEFAULT_PRESET_ID));
  };

  const atCap = userPresets.length >= MAX_USER_PRESETS;
  const isDefault = activeId === DEFAULT_PRESET_ID;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="rounded border border-slate-300 px-2 py-1.5 text-sm bg-white hover:bg-slate-50 text-left min-w-[180px] truncate"
        title={`View: ${activeLabel}`}
      >
        <span className="text-slate-500 text-xs mr-1">View</span>
        <span className="font-medium">{activeLabel}</span>
        <span className="ml-2 text-slate-400" aria-hidden>▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute z-30 right-0 mt-1 w-64 rounded-md border border-slate-300 bg-white shadow-lg p-2 text-sm"
        >
          <div className="px-1 py-1 text-xs uppercase tracking-wide text-slate-500">
            Presets
          </div>
          <button
            type="button"
            onClick={() => switchPreset(DEFAULT_PRESET_ID)}
            className={`w-full text-left px-2 py-1 rounded hover:bg-slate-50 ${
              isDefault ? 'bg-slate-100 font-medium' : ''
            }`}
          >
            Default view
            <span className="ml-2 text-xs text-slate-400">(built-in)</span>
          </button>
          {userPresets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => switchPreset(p.id)}
              className={`w-full text-left px-2 py-1 rounded hover:bg-slate-50 truncate ${
                p.id === activeId ? 'bg-slate-100 font-medium' : ''
              }`}
            >
              {p.name}
            </button>
          ))}

          <div className="my-2 border-t border-slate-100" />

          <button
            type="button"
            disabled={atCap}
            onClick={saveAsNew}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent"
            title={atCap ? 'Max 3 user presets' : 'Save current column setup as a new preset'}
          >
            Save current as new preset…
          </button>
          <button
            type="button"
            disabled={isDefault}
            onClick={saveChanges}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent"
          >
            Save changes to current preset
          </button>
          <button
            type="button"
            disabled={isDefault}
            onClick={deleteCurrent}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent"
          >
            Delete preset
          </button>
          <button
            type="button"
            onClick={resetToDefault}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50"
          >
            Reset to default
          </button>

          {saveMut.isError && (
            <p className="mt-2 text-xs text-rose-600">
              Couldn't save. Try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Exposed so RolloutDetail can initialise its column state from the signed-in
// user's saved preferences without duplicating the "look up preset by id" logic.
export function initialColumnStateFromPrefs(prefs: GridPreferences | undefined): {
  order: ColumnKey[];
  visible: Set<ColumnKey>;
} {
  if (!prefs || prefs.activePresetId === DEFAULT_PRESET_ID) return defaultState();
  const p = prefs.presets.find((p) => p.id === prefs.activePresetId);
  if (!p) return defaultState();
  return applyStoredPreset(p.visibleColumns);
}
